// Iter 145al — Public Daily-Double track record page.
//
// Anonymous-accessible (no auth) so the URL travels well on social.
// Shows aggregate hit rate, ROI, win-streak + a list of every
// settled slip across the window for social proof.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { API } from "../App";
import {
  Coins, ArrowRight, Loader2, CheckCircle2, XCircle, Clock as ClockIcon,
  TrendingUp, Trophy, Flame, Target, Share2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { countryToFlag } from "../lib/countryFlag";

const fmtKick = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric",
    });
  } catch { return iso; }
};

const slipBadge = (o) => {
  if (o === "won") return { Icon: CheckCircle2, cls: "text-emerald-300 bg-emerald-500/15 border-emerald-500/40", text: "WON" };
  if (o === "lost") return { Icon: XCircle, cls: "text-rose-300 bg-rose-500/15 border-rose-500/40", text: "LOST" };
  return { Icon: ClockIcon, cls: "text-muted-foreground bg-foreground/5 border-border", text: "PENDING" };
};

const legBadge = (o) => {
  if (o === "hit") return { Icon: CheckCircle2, cls: "text-emerald-300 bg-emerald-500/15 border-emerald-500/40", text: "HIT" };
  if (o === "miss") return { Icon: XCircle, cls: "text-rose-300 bg-rose-500/15 border-rose-500/40", text: "MISS" };
  return { Icon: ClockIcon, cls: "text-muted-foreground bg-foreground/5 border-border", text: "PENDING" };
};

const WINDOW_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 365, label: "All-time" },
];

export default function DoubleHistoryPage() {
  const [windowDays, setWindowDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Iter 145au — Per-device track record of user-generated slips.
  // Pulled from localStorage (written by DailyDoubleSlipPanel) and
  // graded server-side via the stateless /daily-double/grade-legs.
  const [manualSlips, setManualSlips] = useState([]);
  const [manualLoading, setManualLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    axios
      .get(`${API}/daily-double/history?days=${windowDays}&stake=100`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [windowDays]);

  // Iter 145au — Hydrate manual-slip history from localStorage and
  // batch-grade against the backend.  Independent of `windowDays`
  // because the manual history isn't paginated.
  useEffect(() => {
    let cancelled = false;
    let raw = null;
    try { raw = localStorage.getItem("ndaku_dd_manual_history"); } catch {}
    let entries = [];
    try { entries = raw ? JSON.parse(raw) : []; if (!Array.isArray(entries)) entries = []; } catch { entries = []; }
    if (!entries.length) { setManualSlips([]); return; }
    setManualLoading(true);
    (async () => {
      const out = [];
      for (const e of entries) {
        try {
          const { data: graded } = await axios.post(
            `${API}/daily-double/grade-legs`,
            { legs: e.slip?.legs || [] },
            { timeout: 30000 },
          );
          out.push({
            ...e,
            slip: {
              ...e.slip,
              legs: graded.legs,
              outcome: graded.outcome,
              settled: graded.settled,
            },
          });
        } catch {
          out.push(e);  // surface the un-graded copy on transient error.
        }
        if (cancelled) return;
      }
      if (!cancelled) setManualSlips(out);
    })().finally(() => { if (!cancelled) setManualLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const clearManualHistory = () => {
    try { localStorage.removeItem("ndaku_dd_manual_history"); } catch {}
    setManualSlips([]);
    toast.success("Manual track record cleared");
  };

  const stats = data?.stats || {};
  const slips = data?.slips || [];

  const headline = useMemo(() => {
    const n = stats.n_settled || 0;
    if (n === 0) return "Building the track record · first results land soon.";
    const won = stats.n_won || 0;
    const odds = stats.avg_combined_odds || 2.0;
    return `${won} of last ${n} slips won at ${odds}× average odds.`;
  }, [stats]);

  const sharePage = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const text = `${headline} · NDAKUBET Safe Daily Double`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "NDAKUBET · Daily Double Track Record", text, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch { /* user cancelled */ }
  };

  const profitTone = (stats.profit ?? 0) > 0
    ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/[0.06]"
    : (stats.profit ?? 0) < 0
    ? "text-rose-300 border-rose-500/40 bg-rose-500/[0.06]"
    : "text-muted-foreground border-border bg-foreground/[0.03]";

  const hitTone = (stats.hit_rate ?? 0) >= 60
    ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/[0.06]"
    : (stats.hit_rate ?? 0) >= 45
    ? "text-amber-300 border-amber-500/40 bg-amber-500/[0.06]"
    : "text-rose-300 border-rose-500/40 bg-rose-500/[0.06]";

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="double-history-page">
      <Helmet>
        <title>NDAKUBET · Daily Double Track Record</title>
        <meta name="description" content={headline} />
        <meta property="og:title" content="NDAKUBET · Daily Double Track Record" />
        <meta property="og:description" content={headline} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <header className="border-b border-white/10 bg-[#0d1117]">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-xl sm:text-2xl text-emerald-300 inline-flex items-center gap-2" data-testid="dh-logo">
            <Coins className="w-5 h-5" />
            NDAKUBET<span className="text-muted-foreground font-normal text-sm sm:text-lg"> · Easy Double</span>
          </Link>
          <Link
            to="/halftime"
            className="text-xs sm:text-sm font-bold text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1"
            data-testid="dh-cta"
          >
            Today's slip <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 sm:py-10">
        {/* Hero */}
        <section className="mb-8">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-emerald-300 font-bold mb-2">Public Track Record</p>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-tight" data-testid="dh-headline">
            {headline}
          </h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
            Every slip is built from the platform's existing prediction confidence and graded against real FT/HT scores. No retro-fitting, no cherry-picking — what you see is what landed.
          </p>

          {/* Window selector */}
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {WINDOW_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setWindowDays(opt.value)}
                data-testid={`dh-window-${opt.value}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors border ${
                  windowDays === opt.value
                    ? "bg-emerald-500 text-emerald-950 border-emerald-500"
                    : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-emerald-500/40"
                }`}
              >
                {opt.label}
              </button>
            ))}
            <button
              onClick={sharePage}
              data-testid="dh-share-btn"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border border-border bg-card hover:bg-foreground hover:text-background transition-colors"
            >
              <Share2 className="w-3.5 h-3.5" />
              Share
            </button>
          </div>
        </section>

        {loading && (
          <div className="flex items-center gap-2 text-muted-foreground py-12" data-testid="dh-loading">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading track record…
          </div>
        )}

        {err && !loading && (
          <div className="rounded-2xl border border-rose-500/30 bg-rose-500/[0.06] p-6 text-center" data-testid="dh-error">
            <p className="font-bold text-rose-300">Couldn't load history</p>
            <p className="text-xs text-muted-foreground mt-1">{err}</p>
          </div>
        )}

        {!loading && !err && data && (
          <>
            {/* Stat cards */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-10" data-testid="dh-stats">
              <div className={`rounded-2xl border p-4 sm:p-5 ${hitTone}`} data-testid="dh-stat-hitrate">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 mb-1.5">
                  <Target className="w-3 h-3" /> Hit rate
                </div>
                <div className="font-black text-3xl sm:text-4xl tabular-nums leading-none">
                  {stats.hit_rate != null ? `${stats.hit_rate}%` : "—"}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1.5">
                  {stats.n_won ?? 0} won · {stats.n_lost ?? 0} lost
                </div>
              </div>

              <div className={`rounded-2xl border p-4 sm:p-5 ${profitTone}`} data-testid="dh-stat-roi">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 mb-1.5">
                  <TrendingUp className="w-3 h-3" /> ROI
                </div>
                <div className="font-black text-3xl sm:text-4xl tabular-nums leading-none">
                  {stats.roi != null ? `${stats.roi > 0 ? "+" : ""}${stats.roi}%` : "—"}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1.5">
                  {stats.profit != null
                    ? `${stats.profit > 0 ? "+" : ""}$${Math.abs(stats.profit).toFixed(2)} on $100/slip`
                    : "—"}
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid="dh-stat-streak">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1.5">
                  <Flame className="w-3 h-3" /> Win streak
                </div>
                <div className="font-black text-3xl sm:text-4xl tabular-nums leading-none">
                  {stats.current_win_streak ?? 0}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1.5">
                  best run · {stats.longest_win_streak ?? 0}
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid="dh-stat-avgodds">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1.5">
                  <Trophy className="w-3 h-3" /> Avg odds
                </div>
                <div className="font-black text-3xl sm:text-4xl tabular-nums leading-none">
                  {stats.avg_combined_odds != null ? `${stats.avg_combined_odds.toFixed(2)}×` : "—"}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1.5">
                  {stats.n_settled ?? 0} settled · {stats.n_slips ?? 0} total
                </div>
              </div>
            </section>

            {/* Iter 145au — Your manual slips (device-local track record) */}
            {(manualSlips.length > 0 || manualLoading) && (
              <section className="mb-8" data-testid="dh-manual-section">
                <div className="flex items-end justify-between gap-3 mb-3 flex-wrap">
                  <div>
                    <h2 className="text-lg sm:text-xl font-display font-bold inline-flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-emerald-400" />
                      Your manual slips
                    </h2>
                    <p className="text-[11px] text-muted-foreground mt-0.5 max-w-xl">
                      Slips you generated with Regenerate, graded live against actual HT/FT scores. Stored on this device only.
                    </p>
                  </div>
                  <button
                    onClick={clearManualHistory}
                    data-testid="dh-clear-manual"
                    className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border border-border bg-card hover:bg-foreground hover:text-background transition-colors"
                  >
                    Clear history
                  </button>
                </div>
                {manualLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground py-4 text-xs">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Grading your slips…
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="dh-manual-slip-list">
                    {manualSlips.map((entry) => {
                      const slip = entry.slip;
                      const badge = slipBadge(slip.outcome);
                      const SBIcon = badge.Icon;
                      const stake = 100;
                      const payout = slip.outcome === "won" ? (stake * (slip.combined_odds || 0)) : 0;
                      const genTime = entry.generated_at ? new Date(entry.generated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
                      return (
                        <article
                          key={`${entry.date}-${entry.sig}`}
                          className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.04] overflow-hidden"
                          data-testid={`dh-manual-slip-${entry.sig}`}
                        >
                          <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-emerald-500/20">
                            <div className="flex items-center gap-3 min-w-0 flex-wrap">
                              <div className="font-display font-bold text-sm sm:text-base">{fmtDate(entry.date)}</div>
                              <span className="text-[10px] uppercase tracking-[0.14em] text-emerald-300/80 font-bold">
                                Generated {genTime}
                              </span>
                              <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.16em] px-2 py-0.5 rounded-md border ${badge.cls}`}>
                                <SBIcon className="w-3 h-3" />
                                {badge.text}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 sm:gap-5 shrink-0 text-right">
                              <div>
                                <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground font-bold">Odds</div>
                                <div className="font-display font-bold text-base tabular-nums text-emerald-300">
                                  {slip.combined_odds != null ? `${slip.combined_odds.toFixed(2)}×` : "—"}
                                </div>
                              </div>
                              <div className="hidden sm:block">
                                <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground font-bold">Returns</div>
                                <div className={`font-display font-bold text-base tabular-nums ${slip.outcome === "won" ? "text-emerald-300" : "text-muted-foreground"}`}>
                                  ${payout.toFixed(2)}
                                </div>
                              </div>
                              <div>
                                <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground font-bold">Legs</div>
                                <div className="font-display font-bold text-base tabular-nums">{slip.n_legs || (slip.legs || []).length}</div>
                              </div>
                            </div>
                          </div>
                          <ul className="divide-y divide-emerald-500/10">
                            {(slip.legs || []).map((leg, i) => {
                              const lb = legBadge(leg.outcome);
                              const LBIcon = lb.Icon;
                              return (
                                <li
                                  key={`${entry.sig}-${leg.fixture_id}-${i}`}
                                  className="px-4 sm:px-5 py-3 flex items-center gap-3 text-sm"
                                >
                                  <span className="inline-flex items-center justify-center h-5 min-w-5 px-1 rounded bg-emerald-500/15 text-emerald-300 text-[10px] font-extrabold tabular-nums shrink-0">
                                    L{i + 1}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-semibold truncate">
                                      {leg.home_team?.name} <span className="text-muted-foreground/60 text-xs mx-1">vs</span> {leg.away_team?.name}
                                    </div>
                                    <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mt-0.5">
                                      <span aria-hidden>{countryToFlag(leg.league?.country)}</span>
                                      <span className="truncate">{leg.league?.name}</span>
                                      <span className="opacity-60">·</span>
                                      <span className="tabular-nums">{fmtKick(leg.kickoff)}</span>
                                      <span className="opacity-60">·</span>
                                      <span>{leg.market}: <span className="text-foreground">{leg.prediction}</span></span>
                                      {leg.actual && (
                                        <>
                                          <span className="opacity-60">·</span>
                                          <span className="normal-case text-foreground/80 tabular-nums">{leg.actual}</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <div className="font-display font-bold text-sm tabular-nums text-emerald-300">
                                      {leg.odds != null ? leg.odds.toFixed(2) : "—"}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground tabular-nums">{leg.confidence}%</div>
                                  </div>
                                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-md border shrink-0 ${lb.cls}`}>
                                    <LBIcon className="w-2.5 h-2.5" />
                                    {lb.text}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {/* Slip list */}
            {slips.length === 0 ? (
              <div className="rounded-2xl border border-border bg-card p-10 text-center" data-testid="dh-empty">
                <Coins className="w-10 h-10 mx-auto mb-3 text-emerald-400/60" />
                <p className="font-bold text-lg mb-1">No slips compiled yet</p>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Today's Easy Double rebuilds at 06:55 UTC.  Once the first one settles, results land here automatically.
                </p>
              </div>
            ) : (
              <section className="space-y-3" data-testid="dh-slip-list">
                {slips.map((slip) => {
                  const badge = slipBadge(slip.outcome);
                  const SBIcon = badge.Icon;
                  const stake = 100;
                  const payout = slip.outcome === "won" ? (stake * (slip.combined_odds || 0)) : 0;
                  return (
                    <article
                      key={slip.date}
                      className="rounded-2xl border border-border bg-card hover:border-emerald-500/40 transition-colors overflow-hidden"
                      data-testid={`dh-slip-${slip.date}`}
                    >
                      {/* Slip header */}
                      <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-border/60">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="font-display font-bold text-sm sm:text-base">{fmtDate(slip.date)}</div>
                          <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.16em] px-2 py-0.5 rounded-md border ${badge.cls}`}>
                            <SBIcon className="w-3 h-3" />
                            {badge.text}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 sm:gap-5 shrink-0 text-right">
                          <div>
                            <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground font-bold">Odds</div>
                            <div className="font-display font-bold text-base tabular-nums text-emerald-300">
                              {slip.combined_odds != null ? `${slip.combined_odds.toFixed(2)}×` : "—"}
                            </div>
                          </div>
                          <div className="hidden sm:block">
                            <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground font-bold">Returns</div>
                            <div className={`font-display font-bold text-base tabular-nums ${slip.outcome === "won" ? "text-emerald-300" : "text-muted-foreground"}`}>
                              ${payout.toFixed(2)}
                            </div>
                          </div>
                          <div>
                            <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground font-bold">Legs</div>
                            <div className="font-display font-bold text-base tabular-nums">{slip.n_legs}</div>
                          </div>
                        </div>
                      </div>

                      {/* Legs */}
                      <ul className="divide-y divide-border/60">
                        {(slip.legs || []).map((leg, i) => {
                          const lb = legBadge(leg.outcome);
                          const LBIcon = lb.Icon;
                          return (
                            <li
                              key={`${slip.date}-${leg.fixture_id}-${i}`}
                              className="px-4 sm:px-5 py-3 flex items-center gap-3 text-sm"
                              data-testid={`dh-leg-${slip.date}-${i}`}
                            >
                              <span className="inline-flex items-center justify-center h-5 min-w-5 px-1 rounded bg-emerald-500/15 text-emerald-300 text-[10px] font-extrabold tabular-nums shrink-0">
                                L{i + 1}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold truncate">
                                  {leg.home_team?.name} <span className="text-muted-foreground/60 text-xs mx-1">vs</span> {leg.away_team?.name}
                                </div>
                                <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mt-0.5">
                                  <span aria-hidden>{countryToFlag(leg.league?.country)}</span>
                                  <span className="truncate">{leg.league?.name}</span>
                                  <span className="opacity-60">·</span>
                                  <span className="tabular-nums">{fmtKick(leg.kickoff)}</span>
                                  <span className="opacity-60">·</span>
                                  <span>{leg.market}: <span className="text-foreground">{leg.prediction}</span></span>
                                  {leg.actual && (
                                    <>
                                      <span className="opacity-60">·</span>
                                      <span className="normal-case text-foreground/80 tabular-nums">{leg.actual}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <div className="font-display font-bold text-sm tabular-nums text-emerald-300">
                                  {leg.odds != null ? leg.odds.toFixed(2) : "—"}
                                </div>
                                <div className="text-[10px] text-muted-foreground tabular-nums">{leg.confidence}%</div>
                              </div>
                              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-md border shrink-0 ${lb.cls}`}>
                                <LBIcon className="w-2.5 h-2.5" />
                                {lb.text}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </article>
                  );
                })}
              </section>
            )}

            <p className="text-[11px] text-muted-foreground/70 italic mt-8 max-w-3xl">
              Pending slips are excluded from the ROI maths. Each slip is graded against actual full-time scores (and half-time scores for HT markets) from API-Sports, with re-settles every 15 minutes until final.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
